import RouteBoundary, { ErrorBoundary } from './components/common/RouteBoundary';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, matchPath } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { usesLearnerWorkspace } from './utils/roleWorkspace';
import { usePermissions } from './context/PermissionsContext';

import AuthRoutes from './routes/AuthRoutes.jsx';
import UserRoutes from './routes/UserRoutes.jsx';
import AdminRoutes from './routes/AdminRoutes.jsx';
import PublicCourseDetails from './pages/public/PublicCourseDetails.jsx';
import PublicPostDetails from './pages/public/PublicPostDetails.jsx';
import PrivacyPolicy from './pages/public/PrivacyPolicy.jsx';
import PublicDeleteAccount from './pages/public/PublicDeleteAccount.jsx';
import NotificationRedirect from './pages/common/NotificationRedirect.jsx';
import { NotificationProvider } from './context/NotificationContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { TimezoneProvider } from './context/TimezoneContext';
import { getOrganizationBrandingApi } from './services/auth/authService';
import { applyOrgFavicon, setCachedOrgBranding } from './utils/orgBrandingCache';
import WebPushBootstrap from './components/common/WebPushBootstrap';
import PageNotFound from './pages/error/PageNotFound';

const protectedRoutePatterns = [
  '/zoom-meeting',
  '/admin', '/admin/dashboard', '/admin/profile', '/admin/courses', '/admin/AllCourses',
  '/admin/courses/create', '/admin/courses/edit-create/:courseId',
  '/admin/courses/module/:courseId', '/admin/courses/module/:courseId/content/:moduleId',
  '/admin/courses/module/:courseId/content/:moduleId/document/:contentId',
  '/admin/courses/module/:courseId/content/:moduleId/survey/:contentId',
  '/admin/courses/module/:courseId/content/:moduleId/quiz/:contentId',
  '/admin/courses/module/:courseId/content/:moduleId/video/:contentId',
  '/admin/courses/analytics/:courseId',
  '/admin/courses/analytics/:courseId/CourseAnalyticsAssessmentDetails/:assessmentId',
  '/admin/courses/analytics/:courseId/CourseAnalyticsSurveyDetails/:surveyId',
  '/admin/CourseModule', '/admin/CourseModuleContent', '/admin/CourseVideo',
  '/admin/CourseDocument', '/admin/CourseQuiz', '/admin/CourseSurvey',
  '/admin/roles-and-access', '/admin/approvalRequest', '/admin/classrooms',
  '/admin/classrooms/classroom-dashboard/:classroomId',
  '/admin/classrooms/classroom-dashboard/:classroomId/trainees/:traineeId',
  '/admin/ClassroomAssignments',
  '/admin/classrooms/classroom-dashboard/:classroomId/assingment/:assignmentId/ClassroomAssignmentDetails',
  '/admin/classrooms/assignment-quiz/:classroomId/:assignmentId/:questionId',
  '/admin/ClassroomAssessments',
  '/admin/classrooms/classroom-dashboard/:classroomId/assessment/:assessmentId/ClassroomAssessmentDetails',
  '/admin/classrooms/assignments/quiz-results', '/admin/ClassroomLiveClasses',
  '/admin/ClassroomCommunity', '/admin/question-bank', '/admin/trainees',
  '/admin/traineesAnalytics/:traineeId', '/admin/certificates', '/admin/announcement',
  '/admin/notifications', '/admin/notifications/:id', '/admin/settings', '/admin/prompt-assistant',
  '/user/assessmentQuiz/:encodedAssessmentId', '/user/pdf-viewer', '/user',
  '/user/dashboard', '/user/profile', '/user/AllClassroom',
  '/user/AllClassroom/classroomDetails/:encodedClassroomID',
  '/user/assignmentsQuestions/:encodedAssignmentId',
  '/user/assignment-quiz/:encodedAssignmentId/:encodedQuestionId',
  '/user/assessmentQuestions', '/user/assignmentQuizResult', '/user/courses',
  '/user/courses/courseDetails/:encodedId', '/user/courses/WatchCourse/:encodedId',
  '/user/courses/orderDetails', '/user/courses/orderDetailsWithPayu',
  '/user/courses/successPayUPayment', '/user/courses/cancelPayUPayment',
  '/user/courses/result', '/user/resultvideo', '/user/certificates',
  '/user/notifications', '/user/notifications/notificationDetails', '/user/settings',
  '/user/help', '/user/help/faq', '/user/help/ticket', '/user/feed', '/user/my-feed',
];

const authRoutePatterns = [
  '/', '/auth/login', '/auth/login-2fa', '/auth/register', '/auth/forgot-password',
  '/auth/forgot-password-otp', '/auth/register-otp', '/auth/reset-password',
  '/auth/active-account', '/error/401',
];

function isRouteMatch(patterns, pathname) {
  return patterns.some((path) => matchPath({ path, end: true }, pathname));
}

function UnauthenticatedRoutes({ sessionExpired }) {
  const location = useLocation();
  const isProtectedRoute = isRouteMatch(protectedRoutePatterns, location.pathname);
  const isAuthRoute = isRouteMatch(authRoutePatterns, location.pathname);

  if (isProtectedRoute) {
    const message = sessionExpired
      ? 'Your session has expired. Please log in again.'
      : 'Please log in to access your workspace.';
    return <Navigate to="/auth/login" replace state={{ authMessage: message }} />;
  }

  return isAuthRoute ? <AuthRoutes /> : <PageNotFound />;
}

function isExpiredJwt(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return false; // Support opaque tokens; the API validates those.
    const base64Payload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(base64Payload.padEnd(Math.ceil(base64Payload.length / 4) * 4, '=')));
    return typeof decoded.exp === 'number' && decoded.exp * 1000 <= Date.now();
  } catch {
    return false;
  }
}

function AppRouter() {
  const storedToken = localStorage.getItem('token');
  const expiredToken = Boolean(storedToken && isExpiredJwt(storedToken));
  const token = expiredToken ? null : storedToken;
  const role = localStorage.getItem('role');
  const { permissions } = usePermissions();
  const learnerWorkspace = usesLearnerWorkspace(role, permissions);
  const navigate = useNavigate();
  const location = useLocation();
  const initialLoadRef = useRef(true);
  const [, setBrandingRefreshTick] = useState(0);

  useEffect(() => {
    if (!expiredToken) return;
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('role');
    localStorage.removeItem('organization_details');
    localStorage.removeItem('permissions');
    sessionStorage.clear();
  }, [expiredToken]);

  useEffect(() => {
    // Only redirect on initial load
    if (initialLoadRef.current && token && window.location.pathname === '/') {
      initialLoadRef.current = false;
      if (learnerWorkspace) {
        navigate('/user/dashboard');
      } else {
        // For any other role (admin, trainer, etc.)
        navigate('/admin/dashboard');
      }
    }
  }, [token, role, learnerWorkspace, navigate]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const refreshBranding = async () => {
      try {
        const response = await getOrganizationBrandingApi({
          organization_url: window.location.origin,
        });

        if (cancelled || !response?.success || !response?.data) return;

        const branding = {
          name: response.data.name ?? null,
          logo: response.data.logo ?? null,
          favicon: response.data.favicon ?? null,
        };

        setCachedOrgBranding(branding);
        applyOrgFavicon(branding.favicon, { name: branding.name });

        const existingDashboard = JSON.parse(
          localStorage.getItem('organization_details') || '{}'
        );
        localStorage.setItem(
          'organization_details',
          JSON.stringify({
            ...existingDashboard,
            name: branding.name,
            logo: branding.logo,
            favicon: branding.favicon,
          })
        );

        setBrandingRefreshTick((prev) => prev + 1);
      } catch (error) {
        console.warn('[AppRouter] Failed to refresh organization branding:', error);
      }
    };

    refreshBranding();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Routes>
      {/* Keep authenticated root visits out of the auth 404 route. */}
      {token && (
        <Route
          path="/"
          element={<Navigate to={learnerWorkspace ? '/user/dashboard' : '/admin/dashboard'} replace />}
        />
      )}

      {/* Login stores the token before the success toast finishes. If the browser
          is waiting on its notification-permission prompt, keep the current
          /auth/login URL from falling through to the authenticated 404 route. */}
      {token && (
        <Route
          path="/auth/login"
          element={<Navigate to={learnerWorkspace ? '/user/dashboard' : '/admin/dashboard'} replace />}
        />
      )}

      {/* Public routes - accessible to everyone */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/delete-account" element={<PublicDeleteAccount />} />
      <Route path="/public/courseDetails/:encodedId" element={<PublicCourseDetails />} />
      <Route path="/public/postDetails/:encodedId" element={<PublicPostDetails />} />

      {/* Notification deep-link redirect (resolves web-push clicks) */}
      <Route path="/n/go" element={<NotificationRedirect />} />

      {/* Auth routes - when not logged in */}
      {!token && <Route path="/*" element={<UnauthenticatedRoutes sessionExpired={expiredToken} />} />}

      {/* All signed-in users can use their personal learning workspace.
          Basic custom roles start there; administrative roles retain management. */}
      {token && <Route path="/*" element={
        learnerWorkspace && location.pathname.startsWith('/admin')
          ? <Navigate to="/user/dashboard" replace />
          : learnerWorkspace || location.pathname.startsWith('/user')
            ? <UserRoutes />
            : <AdminRoutes />
      } />}
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary><PermissionsProvider>
      <TimezoneProvider>
        <NotificationProvider>
          <BrowserRouter>
            <WebPushBootstrap />
            <RouteBoundary><AppRouter /></RouteBoundary>
          </BrowserRouter>
        </NotificationProvider>
      </TimezoneProvider>
    </PermissionsProvider></ErrorBoundary>
  );
}

export default App;
